import { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { Truck, Bus, User, School, CalendarClock, Plus, Trash2, Repeat, Clock, Coffee, UserCog, KeyRound, FileSpreadsheet, FileText, FileDown, ChevronDown, ChevronRight } from "lucide-react";
import BusSheet, { busAlertLevel } from "@/components/BusSheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

function useList(path) {
  const [items, setItems] = useState([]);
  const load = () => api.get(path).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  return [items, load];
}

function Field({ label, ...p }) {
  return <div><Label className="text-xs">{label}</Label><Input className="mt-1 h-9" {...p} /></div>;
}

export default function Fleet() {
  const [garages] = useList("/garages");
  const [buses, loadBuses] = useList("/buses");
  const [drivers, loadDrivers] = useList("/drivers");
  const [schools, loadSchools] = useList("/schools");
  const [assignments, loadAssign] = useList("/assignments");
  const [routes, setRoutes] = useState([]);
  useEffect(() => { api.get("/routes").then((r) => setRoutes(r.data)); }, []);

  const [bus, setBus] = useState({ fleet_number: "", plate: "", capacity: 48, garage_id: "" });
  const [drv, setDrv] = useState({ first_name: "", last_name: "", phone: "", employee_number: "", garage_id: "" });
  const [sch, setSch] = useState({ name: "", address: "" });
  const [asg, setAsg] = useState({ route_id: "", driver_id: "", bus_id: "", date: new Date().toISOString().slice(0, 10) });
  const [calendars, setCalendars] = useState([]);
  const [series, loadSeries] = useList("/assignments/series");
  const [sheets, setSheets] = useState([]);
  const [openBus, setOpenBus] = useState(null);
  const monthStart = new Date(); monthStart.setDate(1);
  const [pay, setPay] = useState({ from_date: monthStart.toISOString().slice(0, 10), to_date: new Date().toISOString().slice(0, 10), driver_id: "all" });
  const loadSheets = (f = pay) => api.get("/timesheets", {
    params: { from_date: f.from_date || undefined, to_date: f.to_date || undefined, driver_id: f.driver_id === "all" ? undefined : f.driver_id },
  }).then((r) => setSheets(r.data)).catch(() => {});
  useEffect(() => { loadSheets(); }, []); // eslint-disable-line

  const exportPayroll = async (format) => {
    if (!pay.from_date || !pay.to_date) return toast.error("Choisissez la période (du / au)");
    try {
      const { data } = await api.get("/timesheets/export", {
        params: { from_date: pay.from_date, to_date: pay.to_date, format, driver_id: pay.driver_id === "all" ? undefined : pay.driver_id },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paie_${pay.from_date}_au_${pay.to_date}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(format === "pdf" ? "Rapport PDF téléchargé" : "Fichier Excel téléchargé");
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : e.response?.data?.detail;
      toast.error(apiError(detail));
    }
  };

  const [rec, setRec] = useState({ route_ids: [], driver_id: "", bus_id: "", start_date: "", end_date: "", weekdays: [0, 1, 2, 3, 4] });
  const [replaceFor, setReplaceFor] = useState(null);

  useEffect(() => {
    api.get("/calendars").then((r) => {
      setCalendars(r.data);
      const withYear = r.data.find((c) => c.school_year_start);
      if (withYear) setRec((p) => ({ ...p, start_date: p.start_date || withYear.school_year_start, end_date: p.end_date || withYear.school_year_end }));
    }).catch(() => {});
  }, []);

  const toggleRecRoute = (id) => setRec((p) => ({ ...p, route_ids: p.route_ids.includes(id) ? p.route_ids.filter((x) => x !== id) : [...p.route_ids, id] }));
  const toggleWeekday = (d) => setRec((p) => ({ ...p, weekdays: p.weekdays.includes(d) ? p.weekdays.filter((x) => x !== d) : [...p.weekdays, d].sort() }));

  const createRecurring = async () => {
    if (!rec.route_ids.length || !rec.driver_id || !rec.bus_id) return toast.error("Routes, chauffeur et autobus requis");
    if (!rec.start_date || !rec.end_date) return toast.error("Renseignez la période (ou l'année scolaire dans Administration)");
    try {
      const { data } = await api.post("/assignments/recurring", rec);
      toast.success(`${data.created} journée(s) planifiée(s) · ${data.holidays_skipped} congé(s) ignoré(s)${data.already_assigned ? ` · ${data.already_assigned} déjà assignée(s)` : ""}`);
      loadAssign(); loadSeries();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const replaceDriver = async (aid, driver_id) => {
    try { await api.put(`/assignments/${aid}`, { driver_id }); toast.success("Remplaçant assigné"); setReplaceFor(null); loadAssign(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const create = async (path, body, reset, reload) => {
    try { await api.post(path, body); toast.success("Ajouté"); reset(); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (path, reload) => { await api.delete(path); reload(); };

  return (
    <div>
      <PageHeader title="Flotte & Écoles" subtitle="Autobus, chauffeurs, écoles et assignations" icon={Truck} />
      <div className="p-8">
        <Tabs defaultValue="assignments">
          <TabsList className="mb-6">
            <TabsTrigger value="assignments" data-testid="tab-assign"><CalendarClock className="w-4 h-4 mr-1" /> Assignations</TabsTrigger>
            <TabsTrigger value="buses" data-testid="tab-buses"><Bus className="w-4 h-4 mr-1" /> Autobus</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="tab-drivers"><User className="w-4 h-4 mr-1" /> Chauffeurs</TabsTrigger>
            <TabsTrigger value="recurring" data-testid="tab-recurring"><Repeat className="w-4 h-4 mr-1" /> Horaire récurrent</TabsTrigger>
            <TabsTrigger value="timesheets" data-testid="tab-timesheets"><Clock className="w-4 h-4 mr-1" /> Feuilles de temps</TabsTrigger>
            <TabsTrigger value="schools" data-testid="tab-schools"><School className="w-4 h-4 mr-1" /> Écoles</TabsTrigger>
          </TabsList>

          <TabsContent value="assignments">
            <div className="grid md:grid-cols-5 gap-3 items-end p-4 border border-border rounded-lg mb-4">
              <div><Label className="text-xs">Route</Label>
                <Select value={asg.route_id} onValueChange={(v) => setAsg({ ...asg, route_id: v })}><SelectTrigger className="mt-1" data-testid="asg-route"><SelectValue placeholder="Route" /></SelectTrigger>
                  <SelectContent>{routes.map((r) => <SelectItem key={r.id} value={r.id}>Route {r.route_number}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Chauffeur</Label>
                <Select value={asg.driver_id} onValueChange={(v) => setAsg({ ...asg, driver_id: v })}><SelectTrigger className="mt-1" data-testid="asg-driver"><SelectValue placeholder="Chauffeur" /></SelectTrigger>
                  <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Autobus</Label>
                <Select value={asg.bus_id} onValueChange={(v) => setAsg({ ...asg, bus_id: v })}><SelectTrigger className="mt-1" data-testid="asg-bus"><SelectValue placeholder="Autobus" /></SelectTrigger>
                  <SelectContent>{buses.map((b) => <SelectItem key={b.id} value={b.id}>{b.fleet_number}</SelectItem>)}</SelectContent></Select></div>
              <Field label="Date" type="date" value={asg.date} onChange={(e) => setAsg({ ...asg, date: e.target.value })} />
              <Button onClick={() => create("/assignments", asg, () => setAsg({ ...asg, route_id: "", driver_id: "", bus_id: "" }), loadAssign)} data-testid="create-assign"><Plus className="w-4 h-4 mr-1" /> Assigner</Button>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
              {!assignments.length && <div className="text-sm text-muted-foreground py-8 text-center">Aucune assignation.</div>}
              {assignments.slice(0, 120).map((a, i) => (
                <div key={a.id} className="grid grid-cols-12 gap-3 px-5 py-3 items-center text-sm bg-card" data-testid={`assign-row-${i}`}>
                  <div className="col-span-2 font-medium">Route {a.route_number}</div>
                  <div className="col-span-3 flex items-center gap-2">
                    {a.driver_name}
                    {a.replacement && <Badge variant="outline" className="text-[10px]">remplaçant</Badge>}
                  </div>
                  <div className="col-span-1 font-mono">{a.bus_fleet}</div>
                  <div className="col-span-2 font-mono">{a.date}</div>
                  <div className="col-span-1"><Badge variant="secondary">{a.status}</Badge></div>
                  <div className="col-span-3 text-right flex items-center justify-end gap-2">
                    {replaceFor === a.id ? (
                      <Select onValueChange={(v) => replaceDriver(a.id, v)}>
                        <SelectTrigger className="h-8 w-44" data-testid={`replace-select-${i}`}><SelectValue placeholder="Remplaçant" /></SelectTrigger>
                        <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => setReplaceFor(a.id)} data-testid={`replace-${i}`} title="Remplacer le chauffeur"><UserCog className="w-4 h-4" /></Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => del(`/assignments/${a.id}`, loadAssign)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="recurring">
            <div className="p-4 border border-border rounded-lg mb-4 space-y-4">
              <div className="text-sm text-muted-foreground">
                {calendars.length
                  ? <>Calendriers : {calendars.map((c) => `${c.name} (${(c.holidays || []).length} congés)`).join(" · ")}. Chaque route suit le calendrier de son école.</>
                  : <>Aucun calendrier scolaire (Administration → Calendriers scolaires) : aucun congé ne sera ignoré.</>}
              </div>
              <div>
                <Label className="text-xs">Routes du bloc (plusieurs)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {routes.map((r) => (
                    <button key={r.id} onClick={() => toggleRecRoute(r.id)} data-testid={`rec-route-${r.route_number}`}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${rec.route_ids.includes(r.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                      Route {r.route_number} <span className="opacity-60">{r.period}</span>
                    </button>
                  ))}
                  {!routes.length && <span className="text-xs text-muted-foreground">Importez d'abord des routes.</span>}
                </div>
              </div>
              <div>
                <Label className="text-xs">Jours d'école</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d, i) => (
                    <button key={d} onClick={() => toggleWeekday(i)} data-testid={`rec-day-${i}`}
                      className={`w-14 py-1.5 rounded-md text-xs border ${rec.weekdays.includes(i) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{d}</button>
                  ))}
                </div>
              </div>
              <div className="grid md:grid-cols-5 gap-3 items-end">
                <div><Label className="text-xs">Chauffeur</Label>
                  <Select value={rec.driver_id} onValueChange={(v) => setRec({ ...rec, driver_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="rec-driver"><SelectValue placeholder="Chauffeur" /></SelectTrigger>
                    <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Autobus</Label>
                  <Select value={rec.bus_id} onValueChange={(v) => setRec({ ...rec, bus_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="rec-bus"><SelectValue placeholder="Autobus" /></SelectTrigger>
                    <SelectContent>{buses.map((b) => <SelectItem key={b.id} value={b.id}>{b.fleet_number}</SelectItem>)}</SelectContent>
                  </Select></div>
                <Field label="Du" type="date" value={rec.start_date} onChange={(e) => setRec({ ...rec, start_date: e.target.value })} data-testid="rec-start" />
                <Field label="Au" type="date" value={rec.end_date} onChange={(e) => setRec({ ...rec, end_date: e.target.value })} data-testid="rec-end" />
                <Button onClick={createRecurring} data-testid="create-recurring"><Repeat className="w-4 h-4 mr-1" /> Planifier</Button>
              </div>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
              {!series.length && <div className="text-sm text-muted-foreground py-8 text-center">Aucun horaire récurrent.</div>}
              {series.map((s, i) => (
                <div key={s.id} className="grid grid-cols-12 gap-3 px-5 py-3 items-center text-sm bg-card" data-testid={`series-row-${i}`}>
                  <div className="col-span-3 font-medium">{s.route_ids.length} route(s)</div>
                  <div className="col-span-3 font-mono">{s.start_date} → {s.end_date}</div>
                  <div className="col-span-2">{s.weekdays.map((d) => ["L", "Ma", "Me", "J", "V", "S", "D"][d]).join(" ")}</div>
                  <div className="col-span-3">{s.assignments} journée(s) planifiée(s)</div>
                  <div className="col-span-1 text-right">
                    <Button size="sm" variant="ghost" data-testid={`series-del-${i}`}
                      onClick={async () => { const { data } = await api.delete(`/assignments/series/${s.id}`); toast.success(`${data.deleted} assignation(s) supprimée(s)`); loadAssign(); loadSeries(); }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="timesheets">
            <div className="grid md:grid-cols-5 gap-3 items-end p-4 border border-border rounded-lg mb-4">
              <Field label="Du" type="date" value={pay.from_date} data-testid="pay-from"
                onChange={(e) => { const f = { ...pay, from_date: e.target.value }; setPay(f); loadSheets(f); }} />
              <Field label="Au" type="date" value={pay.to_date} data-testid="pay-to"
                onChange={(e) => { const f = { ...pay, to_date: e.target.value }; setPay(f); loadSheets(f); }} />
              <div><Label className="text-xs">Chauffeur</Label>
                <Select value={pay.driver_id} onValueChange={(v) => { const f = { ...pay, driver_id: v }; setPay(f); loadSheets(f); }}>
                  <SelectTrigger className="mt-1" data-testid="pay-driver"><SelectValue placeholder="Tous" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les chauffeurs</SelectItem>
                    {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <Button onClick={() => exportPayroll("xlsx")} data-testid="export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel (paie)</Button>
              <Button variant="outline" onClick={() => exportPayroll("pdf")} data-testid="export-pdf"><FileText className="w-4 h-4 mr-1" /> Rapport PDF</Button>
              <div className="md:col-span-5 text-xs text-muted-foreground">
                Heures supplémentaires calculées au-delà de 40 h par semaine. Le fichier contient un sommaire par chauffeur et le détail des poinçons.
              </div>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
              <div className="px-5 py-3 bg-secondary grid grid-cols-12 gap-3 text-xs font-semibold uppercase tracking-wide">
                <div className="col-span-3">Chauffeur</div><div className="col-span-2">Date</div>
                <div className="col-span-2">Travaillé</div><div className="col-span-2">Pauses</div>
                <div className="col-span-3">Poinçons</div>
              </div>
              {!sheets.length && <div className="text-sm text-muted-foreground py-8 text-center">Aucun poinçon enregistré.</div>}
              {sheets.map((t, i) => (
                <div key={t.id} className="px-5 py-3 grid grid-cols-12 gap-3 items-center text-sm bg-card" data-testid={`sheet-row-${i}`}>
                  <div className="col-span-3 font-medium">{t.driver_name}</div>
                  <div className="col-span-2 font-mono">{t.date}</div>
                  <div className="col-span-2 font-mono font-bold">{Math.floor(t.worked_min / 60)}h{String(Math.round(t.worked_min % 60)).padStart(2, "0")}</div>
                  <div className="col-span-2 font-mono flex items-center gap-1"><Coffee className="w-4 h-4 text-muted-foreground" />{Math.round(t.break_min)} min</div>
                  <div className="col-span-3 text-xs text-muted-foreground">
                    {(t.entries || []).map((e) => `${{ IN: "Entrée", BREAK_START: "Pause", BREAK_END: "Retour", OUT: "Sortie" }[e.action]} ${e.at.slice(11, 16)}`).join(" · ")}
                    {t.status === "open" && <Badge className="ml-2 bg-emerald-500 text-[10px]">en cours</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="buses">
            <div className="grid md:grid-cols-5 gap-3 items-end p-4 border border-border rounded-lg mb-4">
              <Field label="N° flotte" value={bus.fleet_number} onChange={(e) => setBus({ ...bus, fleet_number: e.target.value })} />
              <Field label="Plaque" value={bus.plate} onChange={(e) => setBus({ ...bus, plate: e.target.value })} />
              <Field label="Capacité" type="number" value={bus.capacity} onChange={(e) => setBus({ ...bus, capacity: +e.target.value })} />
              <GarageSelect garages={garages} value={bus.garage_id} onChange={(v) => setBus({ ...bus, garage_id: v })} testid="bus-garage" />
              <Button onClick={() => create("/buses", bus, () => setBus({ fleet_number: "", plate: "", capacity: 48, garage_id: bus.garage_id }), loadBuses)} data-testid="create-bus"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
              {!buses.length && <div className="text-sm text-muted-foreground py-8 text-center">Aucun autobus.</div>}
              {buses.map((b, i) => {
                const lvl = busAlertLevel(b);
                const open = openBus === b.id;
                return (
                  <div key={b.id} className="bg-card" data-testid={`bus-row-${i}`}>
                    <div className="grid grid-cols-12 gap-3 px-5 py-3 items-center text-sm cursor-pointer hover:bg-secondary/50"
                      onClick={() => setOpenBus(open ? null : b.id)} data-testid={`bus-toggle-${i}`}>
                      <div className="col-span-1">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</div>
                      <div className="col-span-2 font-medium font-mono">{b.fleet_number}</div>
                      <div className="col-span-2">{b.plate || "—"}</div>
                      <div className="col-span-4 text-muted-foreground">{b.capacity} places · {b.garage_name || "—"}{b.make ? ` · ${b.make}` : ""}</div>
                      <div className="col-span-2 flex items-center gap-2">
                        <Badge variant="secondary">{b.status}</Badge>
                        {lvl === "expired" && <Badge className="bg-red-600 text-[10px]" data-testid={`bus-alert-expired-${i}`}>date échue</Badge>}
                        {lvl === "soon" && <Badge className="bg-orange-500 text-[10px]" data-testid={`bus-alert-soon-${i}`}>échéance proche</Badge>}
                      </div>
                      <div className="col-span-1 text-right">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); del(`/buses/${b.id}`, loadBuses); }}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    {open && <BusSheet bus={b} garages={garages} onSaved={() => loadBuses()} />}
                  </div>
                );
              })}
            </div>

          </TabsContent>

          <TabsContent value="drivers">
            <div className="grid md:grid-cols-6 gap-3 items-end p-4 border border-border rounded-lg mb-4">
              <Field label="Prénom" value={drv.first_name} onChange={(e) => setDrv({ ...drv, first_name: e.target.value })} />
              <Field label="Nom" value={drv.last_name} onChange={(e) => setDrv({ ...drv, last_name: e.target.value })} />
              <Field label="Téléphone" value={drv.phone} onChange={(e) => setDrv({ ...drv, phone: e.target.value })} />
              <Field label="N° employé" value={drv.employee_number} onChange={(e) => setDrv({ ...drv, employee_number: e.target.value })} />
              <GarageSelect garages={garages} value={drv.garage_id} onChange={(v) => setDrv({ ...drv, garage_id: v })} testid="driver-garage" />
              <div className="md:col-span-6 flex flex-wrap items-center gap-3">
                <Button onClick={() => create("/drivers", drv, () => setDrv({ first_name: "", last_name: "", phone: "", employee_number: "", garage_id: drv.garage_id }), loadDrivers)} data-testid="create-driver"><Plus className="w-4 h-4 mr-1" /> Ajouter le chauffeur</Button>
                <span className="text-xs text-muted-foreground">Ni courriel ni mot de passe : un NIP de 6 chiffres est généré automatiquement — le chauffeur se connecte avec ce seul NIP.</span>
                <Button variant="outline" size="sm" className="ml-auto" data-testid="export-pins"
                  onClick={async () => {
                    try {
                      const r = await api.get("/drivers/pins/export", { responseType: "blob" });
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement("a");
                      a.href = url; a.download = "NIP_chauffeurs.pdf"; a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
                  }}>
                  <FileDown className="w-4 h-4 mr-1" /> Feuille des NIP (PDF)
                </Button>
              </div>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
              {!drivers.length && <div className="text-sm text-muted-foreground py-8 text-center">Aucun chauffeur.</div>}
              {drivers.map((d, i) => (
                <div key={d.id} className="bg-card px-5 py-3 text-sm" data-testid={`driver-row-${i}`}>
                  <div className="grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-3 font-medium">{d.first_name} {d.last_name}</div>
                    <div className="col-span-2">{d.phone || "—"}</div>
                    <div className="col-span-3 truncate text-muted-foreground">{d.garage_name || "—"}{d.employee_number ? ` · n° ${d.employee_number}` : ""}</div>
                    <div className="col-span-2 flex items-center gap-2">
                      {d.pin ? (
                        <button title="Copier le NIP" data-testid={`driver-pin-${i}`}
                          onClick={() => { navigator.clipboard?.writeText(d.pin); toast.success("NIP copié"); }}
                          className="font-mono text-lg font-bold tracking-widest text-primary hover:underline">
                          {d.pin}
                        </button>
                      ) : <Badge variant="outline" data-testid={`access-none-${i}`}>aucun NIP</Badge>}
                    </div>
                    <div className="col-span-2 text-right flex justify-end gap-1">
                      <Button size="sm" variant="ghost" data-testid={`regen-pin-${i}`} title="Générer un nouveau NIP"
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/drivers/${d.id}/pin`);
                            toast.success(`Nouveau NIP : ${data.pin}`);
                            loadDrivers();
                          } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
                        }}>
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(`/drivers/${d.id}`, loadDrivers)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="schools">
            <div className="grid md:grid-cols-3 gap-3 items-end p-4 border border-border rounded-lg mb-4">
              <Field label="Nom" value={sch.name} onChange={(e) => setSch({ ...sch, name: e.target.value })} />
              <Field label="Adresse" value={sch.address} onChange={(e) => setSch({ ...sch, address: e.target.value })} />
              <Button onClick={() => create("/schools", sch, () => setSch({ name: "", address: "" }), loadSchools)} data-testid="create-school"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
            </div>
            <List items={schools} render={(s) => [s.name, s.address, `Arrivée max ${s.bus_arrival_max}`, ""]}
              onDel={(s) => del(`/schools/${s.id}`, loadSchools)} testid="school" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function GarageSelect({ garages, value, onChange, testid }) {
  return (
    <div><Label className="text-xs">Garage</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testid}><SelectValue placeholder="Choisir" /></SelectTrigger>
        <SelectContent>{garages.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function List({ items, render, onDel, testid }) {
  if (!items.length) return <div className="text-sm text-muted-foreground py-8 text-center border border-dashed rounded-lg">Aucun élément.</div>;
  return (
    <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
      {items.map((it, i) => (
        <div key={it.id || i} className="grid grid-cols-5 gap-3 px-5 py-3 items-center text-sm bg-card" data-testid={`${testid}-row-${i}`}>
          {render(it).map((c, j) => <div key={j} className={j === 0 ? "font-medium" : ""}>{c}</div>)}
          <div className="text-right"><Button size="sm" variant="ghost" onClick={() => onDel(it)}><Trash2 className="w-4 h-4" /></Button></div>
        </div>
      ))}
    </div>
  );
}
